import {
  createContext,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
} from "react";
import { formatIsoDate } from "src/utils/dateTime";
import { useDateURL, type GlobalDates } from "src/utils/hooks/useDateURL";

export type FilterStops = "direct" | "onestop" | "twostops";
type FilterDepartureStations = string[];
type FilterArrivalStations = string[];
export type FilterTimeRange = {
  earliest: string;
  latest: string;
};
const flightSortOptions = "best";
export type SortingOptions =
  | typeof flightSortOptions
  | "cheapest"
  | "fastest"
  | "soonest";
export type FilterOptions = {
  stops: FilterStops[];
  price?: FilterPriceRange;
  outboundTime?: FilterTimeRange;
  duration?: FilterDurationRange;
  departureStations?: FilterDepartureStations;
  arrivalStations?: FilterArrivalStations;
};
export type FilterDurationRange = {
  shortest: number;
  longest: number;
};
export type FilterPriceRange = {
  lowest: number;
  highest: number;
};

export const DEFAULT_SORT = "soonest";
export const DEFAULT_FILTER: FilterOptions = {
  stops: [],
};

type TravelSearchSettingsProviderProps = {
  sort?: SortingOptions;
  filter?: FilterOptions;
};

export function TravelSearchSettingsProvider(
  props: PropsWithChildren<TravelSearchSettingsProviderProps>,
) {
  const [sort, setSort] = useState<SortingOptions | undefined>(props.sort);
  const [filter, setFilter] = useState(props.filter ?? DEFAULT_FILTER);
  const { setGlobalDates, getGlobalDates } = useDateURL();

  const providerValue = useMemo(() => {
    const getActiveFilters = () => {
      return Object.keys(filter).filter((key) => {
        if (key === "stops") {
          return filter.stops.length > 0;
        }
        return filter[key as keyof FilterOptions] !== undefined;
      });
    };

    const { departureDate, returnDate } = getGlobalDates();
    const dateObject = {
      departureDate: departureDate ? formatIsoDate(departureDate) : "",
      returnDate: returnDate ? formatIsoDate(returnDate) : "",
    };

    return {
      dateObject,
      sort,
      filter,
      setDateObject: setGlobalDates,
      setSort,
      setFilter,
      getActiveFilters,
    };
  }, [getGlobalDates, sort, filter, setGlobalDates]);

  return (
    <TravelSearchContext value={providerValue}>
      {props.children}
    </TravelSearchContext>
  );
}

export function useTravelSearchSettings() {
  const context = useContext(TravelSearchContext);
  if (!context) {
    throw new Error(
      `useTravelSearchSettings must be used within TravelSearchSettingsProvider`,
    );
  }
  return context;
}

const TravelSearchContext = createContext<{
  dateObject: {
    departureDate: string;
    returnDate?: string;
  };
  sort: SortingOptions | undefined;
  filter: FilterOptions;
  setDateObject(
    datesOrUpdater: GlobalDates | ((prev: GlobalDates) => GlobalDates),
  ): void;
  setSort: Dispatch<SetStateAction<SortingOptions | undefined>>;
  setFilter: Dispatch<SetStateAction<FilterOptions>>;
  getActiveFilters: () => string[];
}>({
  dateObject: {
    departureDate: "",
  },
  sort: DEFAULT_SORT,
  filter: DEFAULT_FILTER,
  setDateObject: () => {},
  setSort: () => {},
  setFilter: () => {},
  getActiveFilters: () => [],
});
